import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { XCircle, CheckCircle2, Loader2, Calendar, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

export default function AppointmentConfirmCancel() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [cancelled, setCancelled] = useState(false);
  const [autoCancelTriggered, setAutoCancelTriggered] = useState(false);

  const { data: booking, isLoading, error } = useQuery(
    ['appointments', 'confirm-cancel', token],
    async () => {
      const response = await api.get(`/appointments/public/confirm-cancel/${token}`);
      return response.data.data;
    },
    { enabled: !!token, retry: 1 }
  );

  const confirmCancelMutation = useMutation(
    async () => {
      const response = await api.post(`/appointments/public/confirm-cancel/${token}`);
      return response.data;
    },
    {
      onSuccess: () => {
        setCancelled(true);
        toast.success('Termin erfolgreich storniert!');
        setTimeout(() => navigate('/appointments/all'), 3000);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler bei der Stornierung');
      }
    }
  );

  useEffect(() => {
    if (!token || !booking) return;
    if (cancelled || booking.status === 'cancelled') return;
    if (confirmCancelMutation.isLoading || autoCancelTriggered) return;
    setAutoCancelTriggered(true);
    confirmCancelMutation.mutate();
  }, [
    token,
    booking,
    cancelled,
    confirmCancelMutation.isLoading,
    confirmCancelMutation.mutate,
    autoCancelTriggered
  ]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    const errMsg = (error as any)?.response?.data?.error;
    const isExpired = (error as any)?.response?.status === 410 || errMsg?.includes('abgelaufen');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">
            {isExpired ? 'Link abgelaufen' : 'Link ungültig'}
          </h1>
          <p className="text-gray-600 mb-4">
            {errMsg || 'Dieser Bestätigungslink ist ungültig oder wurde bereits verwendet. Die Stornierung muss innerhalb von 30 Minuten bestätigt werden.'}
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  if (cancelled || booking.status === 'cancelled') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md bg-white rounded-lg shadow-lg p-8">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Termin storniert!</h1>
          <p className="text-gray-600 mb-4">
            Der Termin wurde erfolgreich storniert. Sie werden zur Übersicht weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold mb-4">Stornierung bestätigen</h1>

        <div className="bg-gray-50 rounded p-4 mb-6">
          <div className="mb-3">
            <div className="text-sm text-gray-600 mb-1">Termin</div>
            <div className="font-semibold">
              {format(parseISO(booking.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} –{' '}
              {format(parseISO(booking.end_time), 'HH:mm', { locale: de })}
            </div>
          </div>
          <div className="mb-3">
            <div className="text-sm text-gray-600 mb-1">Lehrer</div>
            <div className="font-semibold">
              {booking.teacher_first_name} {booking.teacher_last_name}
            </div>
          </div>
          {booking.student_name && (
            <div>
              <div className="text-sm text-gray-600 mb-1">Schüler</div>
              <div className="font-semibold">{booking.student_name}</div>
            </div>
          )}
        </div>

        <div className="bg-red-50 border border-red-200 rounded p-3 mb-6 text-sm text-red-800">
          {confirmCancelMutation.isLoading
            ? 'Stornierung läuft automatisch. Bitte warten...'
            : 'Die Stornierung wird automatisch ausgelöst. Falls nötig, können Sie unten erneut bestätigen.'}
        </div>

        {confirmCancelMutation.isLoading && (
          <div className="w-full px-4 py-2 bg-red-600 text-white rounded flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Wird storniert...
          </div>
        )}

        {confirmCancelMutation.isError && (
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/appointments/all')}
              className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              onClick={() => confirmCancelMutation.mutate()}
              disabled={confirmCancelMutation.isLoading}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <XCircle className="w-5 h-5" />
              Erneut bestätigen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
